import { createFileRoute } from "@tanstack/react-router";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, serviceSchema, speakable, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad gör ett filmproduktionsbolag?",
    a: "Ett filmproduktionsbolag planerar, spelar in och färdigställer film åt en uppdragsgivare. Det innefattar manus, produktionsledning, casting, location, inspelning med regi, foto, ljus och ljud, samt post-production med klipp, grade, ljudmix, undertexter och versionering.",
  },
  {
    q: "Vad kostar ett filmproduktionsbolag i Sverige?",
    a: "Riktpriser 2026: kortformat och social-content 40 000–120 000 kr, företagsfilm 80 000–250 000 kr, reklamfilm 150 000–450 000 kr, brand-film och dokumentär 250 000–700 000 kr. Löpande produktion via abonnemang startar på 14 995 kr/mån.",
  },
  {
    q: "Vad ska ingå i offerten?",
    a: "Fast pris, antal inspelningsdagar, antal leveransversioner och format, antal revisionsrundor, musiklicens, rättigheter och tidsplan med leveransdatum. Saknas något av detta blir slutnotan nästan alltid högre än offerten.",
  },
  {
    q: "Hur lång tid tar en produktion?",
    a: "Från godkänd brief till leverans: 2–3 veckor för kortformat, 3–5 veckor för företagsfilm, 4–6 veckor för reklamfilm och 5–8 veckor för dokumentär. Pulse-abonnemang levererar första klippet inom 10–14 dagar.",
  },
  {
    q: "Behöver vi manus innan vi kontaktar er?",
    a: "Nej. De flesta kommer med ett affärsmål, inte ett manus. Vi tar fram koncept och manus som del av produktionen och ni godkänner skriftligt innan inspelning bokas.",
  },
  {
    q: "Äger vi materialet efteråt?",
    a: "Ja. Allt slutmaterial är ert att använda fritt i egna kanaler — sajt, sociala medier, sälj och rekrytering. Musiklicenser specificeras separat i offerten.",
  },
];

export const Route = createFileRoute("/filmproduktionsbolag")({
  head: () => ({
    meta: [
      { title: "Filmproduktionsbolag — 7 saker offerten måste innehålla" },
      {
        name: "description",
        content:
          "Vad ett filmproduktionsbolag gör, vad det kostar 2026 och vad offerten ska innehålla. Fenny producerar reklamfilm, företagsfilm och dokumentär med eget team.",
      },
      { property: "og:title", content: "Filmproduktionsbolag — 7 saker offerten måste innehålla" },
      {
        property: "og:description",
        content:
          "Prisspann, tidsplan och checklista för offerten — plus hur Fenny producerar med eget team i hela Sverige.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/filmproduktionsbolag" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/filmproduktionsbolag" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/filmproduktionsbolag", name: "Filmproduktionsbolag — 7 saker offerten måste innehålla", description: "Vad ett filmproduktionsbolag gör, vad det kostar 2026 och vad offerten ska innehålla. Fenny producerar reklamfilm, företagsfilm och dokumentär med eget team."})) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Filmproduktionsbolag",
            path: "/filmproduktionsbolag",
            serviceType: "Filmproduktion",
            description:
              "Filmproduktionsbolag för reklamfilm, företagsfilm, employer branding och dokumentär i hela Sverige.",
            priceRange: "40 000–700 000 kr",
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Filmproduktionsbolag", path: "/filmproduktionsbolag" },
          ]),
        ),
      },
    ],
  }),
  component: FilmproduktionsbolagPage,
});

function FilmproduktionsbolagPage() {
  return (
    <LandingPage
      eyebrow="Filmproduktionsbolag"
      title={<>Ett filmproduktionsbolag som levererar på datum, inte på känsla.</>}
      lede="Fenny är ett filmproduktionsbolag med eget team för hela kedjan: manus, produktionsledning, inspelning och post. Ni får fast pris, en tidsplan med leveransdatum och en kontaktperson genom hela produktionen."
      tldr={
        <p>
          Ett filmproduktionsbolag ansvarar för hela genomförandet — från manus
          och planering till inspelning och färdig leverans. Prisspann i Sverige
          2026: <strong>40 000–700 000 kr</strong> per produktion beroende på
          format och omfattning. Löpande behov täcks av{" "}
          <strong>abonnemang från 14 995 kr/mån</strong>.
        </p>
      }
      ctaPrimary={{ label: "Boka strategimöte", to: "/kontakt" }}
      ctaSecondary={{ label: "Se priser", to: "/priser" }}
      trackingId="filmproduktionsbolag"
      bullets={[
        { title: "Fast pris i offerten", body: "Inspelningsdagar, versioner, revideringar och rättigheter specificerade innan start." },
        { title: "En kontaktperson", body: "Samma producent från brief till leverans. Inga överlämningar mitt i projektet." },
        { title: "Eget team", body: "Regi, foto, ljus, ljud, klipp och grade inhouse — jämn kvalitet mellan produktioner." },
      ]}
      pricing={{
        heading: "Prisspann 2026",
        intro: "Riktpriser exklusive moms för produktion i Sverige.",
        rows: [
          { format: "Kortformat / social", scope: "1 dag, flera klipp, versionering", price: "40 000–120 000 kr", lead: "2–3 veckor" },
          { format: "Företagsfilm", scope: "Manus, 1–2 dagar, klipp och grade", price: "80 000–250 000 kr", lead: "3–5 veckor" },
          { format: "Reklamfilm", scope: "Koncept, casting, regi, post", price: "150 000–450 000 kr", lead: "4–6 veckor" },
          { format: "Dokumentär / brand-film", scope: "Research, flera dagar, ljudmix", price: "250 000–700 000 kr", lead: "5–8 veckor" },
          { format: "Abonnemang (Pulse)", scope: "2–8 klipp per månad, löpande", price: "från 14 995 kr/mån", lead: "10–14 dagar till första klipp" },
        ],
        footnote: "Alla priser exklusive moms. Resa inom Sverige ingår i abonnemang.",
      }}
      process={{
        heading: "Produktionsprocessen",
        steps: [
          { label: "Brief", body: "Mål, målgrupp, kanal och budget. Vi säger till direkt om formatet är fel för målet.", duration: "dag 1–3" },
          { label: "Manus och plan", body: "Koncept, manus och produktionsplan för skriftligt godkännande.", duration: "vecka 1–2" },
          { label: "Pre-production", body: "Casting, location, tillstånd, teknik och schema.", duration: "vecka 2–3" },
          { label: "Inspelning", body: "Regi, foto, ljus och ljud. Typiskt 1–2 dagar.", duration: "vecka 3–4" },
          { label: "Post och leverans", body: "Klipp, grade, ljudmix, undertexter och versioner för varje kanal.", duration: "vecka 4–6" },
        ],
      }}
      sections={[
        {
          id: "vad-ingar",
          heading: "Vad ingår när ni anlitar ett filmproduktionsbolag?",
          body: (
            <>
              <p>
                Produktionsledning, manus, casting och location, teknik och team
                på inspelningsdagen, samt post-production med klipp, färgläggning,
                ljudmix, undertexter och versionering för varje kanal ni
                publicerar i.
              </p>
              <p>
                Det som ofta faller utanför standardoffert och bör klargöras:
                antal revisionsrundor, musiklicens, skådespelarersättning vid
                längre användningstid, samt rättigheter för annonsering utanför
                Sverige.
              </p>
            </>
          ),
        },
        {
          id: "format",
          heading: "Vilka format producerar vi?",
          body: (
            <p>
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilm">Reklamfilm</a>,{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/foretagsfilm">företagsfilm</a>,{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/employer-branding-film">employer branding</a>,{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/produktfilm">produktfilm</a>,{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/motion-graphics">animation och motion graphics</a> samt löpande{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/sociala-medier-film">social-content</a>.
            </p>
          ),
        },
        {
          id: "var",
          heading: "Var vi producerar",
          body: (
            <p>
              Hela Sverige, med team i{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/produktionsbolag/stockholm">Stockholm</a>,{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/produktionsbolag/goteborg">Göteborg</a> och{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/produktionsbolag/malmo">Malmö</a>. Läs mer om vad ett{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/filmbolag">filmbolag</a> gör och hur rollerna skiljer sig åt.
            </p>
          ),
        },
      ]}
      faq={FAQ}
      caseLimit={6}
      riskReducers
      closing={{
        title: "Behöver ni ett filmproduktionsbolag?",
        body: "Skicka er brief så återkommer vi med fast pris och tidsplan inom 48 timmar.",
        primary: { label: "Boka strategimöte", to: "/kontakt" },
        secondary: { label: "Se case", to: "/case" },
      }}
    />
  );
}
